import React from "react"
import { graphql, useStaticQuery } from "gatsby"
import Image from "gatsby-image"
import styled, { css } from "styled-components"
import { Layout } from "../../components/Layout"
import { IconBack } from "../../components/blog/BlogIcons"
import { InnerLinkButton } from "../../components/blog/BlogLinkButton"
import {
  StyledWrap,
  StyledTitleWrapper,
  StyledContentsWrapper,
  StyledLinkWrapper,
} from "../../components/blog/BlogStyles"
import { media } from "../../styles/media"
import SEO from "../../components/SEO"

const StyledHeroSection = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 40px;
  margin-bottom: 40px;
  background: linear-gradient(
    to bottom right,
    #f0f4f8 0%,
    #f0f4f8 50%,
    #ffffff 50%,
    #ffffff 100%
  );
  ${() =>
    media.tablet(css`
      padding: 40px 30px;
    `)}
  ${() =>
    media.sp(css`
      padding: 30px 20px;
      background: linear-gradient(
        to bottom right,
        #f0f4f8 0%,
        #f0f4f8 60%,
        #ffffff 60%,
        #ffffff 100%
      );
    `)}
`

const StyledKenkoTitle = styled.div`
  color: #1a3a83;
  font-size: 2.2em;
  font-weight: normal;
  text-align: center;
  position: relative;
  z-index: 1;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  font-family: Arial, Helvetica, sans-serif;

  span:last-child {
    font-size: 1.2em;
    margin-top: 5px;
  }

  ${() =>
    media.tablet(css`
      font-size: 1.8em;
    `)}
  ${() =>
    media.sp(css`
      font-size: 1.3em;
      margin-bottom: 30px;
    `)}
`

const StyledNewsVisual = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 20px;
  width: 100%;
  .visual-logo {
    width: 100%;
    max-width: 280px;
  }
  .visual-certificate {
    width: 100%;
    max-width: 350px;
    .gatsby-image-wrapper {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
  ${() =>
    media.sp(css`
      flex-direction: column;
      .visual-logo,
      .visual-certificate {
        max-width: 100%;
      }
    `)}
`

const StyledIntroText = styled.p`
  line-height: 1.8;
  margin-bottom: 40px;
  word-break: keep-all;
  overflow-wrap: anywhere;
`

const StyledCertificationInfo = styled.div`
  .sub-title {
    border-left: 4px solid #1a3a83;
    padding-left: 15px;
    font-size: 14px;
    color: #1a3a83;
    margin-bottom: 20px;
    span {
      display: block;
      font-size: 22px;
      font-weight: bold;
      color: #333;
      margin-top: 5px;
    }
  }
  p {
    line-height: 1.8;
  }
`

const HealthManagement2026 = () => {
  const data = useStaticQuery(graphql`
    {
      logo: file(relativePath: { eq: "kenko-keiei-logo-2026.png" }) {
        childImageSharp {
          fluid(quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      certificate: file(
        relativePath: { eq: "kenko-keiei-certificate-2026.jpg" }
      ) {
        childImageSharp {
          fluid(quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `)

  return (
    <Layout>
      <SEO>
        <title>
          健康経営優良法人2026（中小規模法人部門）に認定されました｜フランソア
        </title>
        <meta
          name="description"
          content="経済産業省が実施する「健康経営優良法人認定制度」において、フランソアが「健康経営優良法人2026（中小規模法人部門）」の認定を取得しました。"
        />
      </SEO>
      <StyledWrap>
        <StyledTitleWrapper>
          <h2 className="title">
            健康経営優良法人2026（中小規模法人部門）に認定されました
          </h2>
          <span className="pubDate">2026.04.21</span>
        </StyledTitleWrapper>

        <StyledContentsWrapper>
          <StyledHeroSection>
            <StyledKenkoTitle>
              <span>KENKO Investment for Health</span>
              <span>2026</span>
            </StyledKenkoTitle>
            <StyledNewsVisual>
              <div className="visual-logo">
                <Image
                  fluid={data.logo.childImageSharp.fluid}
                  alt="健康経営優良法人2026 ロゴ"
                />
              </div>
              <div className="visual-certificate">
                <Image
                  fluid={data.certificate.childImageSharp.fluid}
                  alt="健康経営優良法人2026 認定証"
                />
              </div>
            </StyledNewsVisual>
          </StyledHeroSection>

          <StyledIntroText>
            このたび、経済産業省が実施する「健康経営優良法人認定制度」において、
            「健康経営優良法人2026（中小規模法人部門）」の認定を取得しました。
          </StyledIntroText>

          <StyledCertificationInfo>
            <h2 className="sub-title">
              ABOUT THE CERTIFICATION
              <span>健康経営優良法人認定制度について</span>
            </h2>
            <p>
              健康経営優良法人認定制度とは、従業員の健康維持・増進につながる優良な取り組みを実践している大企業や中小企業を「健康経営優良法人」として認定し、顕彰する制度です。
            </p>
            <p>
              フランソアは今後も、従業員が生き生きと働き続けられるよう、グループ一丸となって健康づくりに取り組んでまいります。
            </p>
          </StyledCertificationInfo>
        </StyledContentsWrapper>

        <StyledLinkWrapper>
          <InnerLinkButton to="/">
            <IconBack />
            トップに戻る
          </InnerLinkButton>
        </StyledLinkWrapper>
      </StyledWrap>
    </Layout>
  )
}

export default HealthManagement2026
